import './styles.css'
import React from 'react'
import moment from 'moment'
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  Header,
  Icon,
  Loader,
  Message,
  Popup,
} from 'semantic-ui-react'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import ExportCSVButton from './ExportCSVButton/ExportCSVButton'
import ReactDatepicker, {
  validateDate,
} from '../../../../../common-components/rewrite/ReactDatepicker'
import {
  BOOKINGS_STATUS,
  BOOKINGS_CUSTOMER_NAME,
  BOOKINGS_POSTAL_CODE,
  BOOKINGS_START_DATE,
  BOOKINGS_END_DATE,
  RESET_FILTERS,
  BOOKINGS_ASSIGNED_COLLECTOR,
  BOOKINGS_PREFERRED_COLLECTOR,
  BOOKINGS_CREATED_BY,
  BOOKINGS_ASSIGNED_BY,
  BOOKINGS_VENUE_STATE,
  REFERENCE_ID,
} from '../../utils/actionTypes'
import { useStatusOptions } from '../../utils/useStatusOptions'
import { useCollectorOptions } from '../../utils/useCollectorOptions'

const getDispatchPayloadForExtraFilters = extraField => {
  const mapping = {
    assignedCollector: BOOKINGS_ASSIGNED_COLLECTOR,
    preferredCollector: BOOKINGS_PREFERRED_COLLECTOR,
    createdBy: BOOKINGS_CREATED_BY,
    assignedBy: BOOKINGS_ASSIGNED_BY,
    state: BOOKINGS_VENUE_STATE,
  }
  return mapping[extraField] || null
}

// eslint-disable-next-line prefer-arrow-callback
const TextInputClearIcon = React.memo(function TextInputClearIcon({ onClick, label, preventBlur }) {
  return (
    <Icon
      name="close"
      link
      aria-label={label}
      title={label}
      onMouseDown={preventBlur}
      onClick={onClick}
    />
  )
})

const KegCollectionBookingsTableFiltersForm = props => {
  const {
    translate,
    dispatchBookingsAPIParams,
    formatDateForApiCall,
    defaultDisplayedDateFormat,
    onForceRefresh,
    isFetching,
    collectorsList,
    extraFieldsEnabled,
    setExtraFieldsEnabled,
    isExtraFilterEnabled,
    fieldValues,
    setFieldValues,
    resetFieldValues,
    fieldValuesInitialState,
    bookingsList,
    bookingsExportList,
    i18n,
    dispatch,
    bookingsAPIParams,
    bookingsListViewSearchFilterOptions,
  } = props

  const [refreshing, setRefreshing] = React.useState(false)
  const { locale: localeCode } = i18n

  // Options for dropdown filters and extra filters
  const statusOptions = useStatusOptions(translate)
  const collectorOptions = useCollectorOptions(collectorsList?.content, translate)

  const extraFilterOptions = React.useMemo(
    () => [
      {
        key: 'assignedCollector',
        labelKey: translate('assignedCollector'),
        id: 'assignedCollector',
      },
      {
        key: 'preferredCollector',
        labelKey: translate('preferredCollector'),
        id: 'preferredCollector',
      },
      { key: 'createdBy', labelKey: translate('createdBy'), id: 'createdBy' },
      {
        key: 'assignedBy',
        labelKey: translate('assignedBy'),
        id: 'assignedBy',
      },
      { key: 'state', labelKey: translate('State'), id: 'state' },
    ],
    [translate]
  )

  const buildFilterOptions = React.useCallback(
    (data, keyPrefix, mapFunc, additionalDefaults = []) => {
      const defaults = [{ key: `${keyPrefix}_all`, text: translate('ALL'), value: '' }]
      if (additionalDefaults.length > 0) {
        defaults.push(...additionalDefaults)
      }
      if (!data || data.length === 0) {
        return defaults
      }
      const options = data.map(mapFunc)
      return [...defaults, ...options.sort((a, b) => a.text.localeCompare(b.text))]
    },
    [translate]
  )

  // Helpers
  const getDropdownPlaceholder = React.useCallback(
    (isFetchingOptions, didFetchFailOptions, loadingKey, failedKey, options) => {
      if (isFetchingOptions) return translate(loadingKey)
      if (didFetchFailOptions || options.length === 0) return translate(failedKey)
      return translate('ALL')
    },
    [translate]
  )

  const isResetDisabled = React.useMemo(
    () => isEqual(fieldValues, fieldValuesInitialState),
    [fieldValues, fieldValuesInitialState]
  )

  // Debounced dispatcher for venue name input
  const debouncedDispatchVenue = React.useMemo(
    () =>
      debounce(value => dispatchBookingsAPIParams({ type: BOOKINGS_CUSTOMER_NAME, value }), 500),
    [dispatchBookingsAPIParams]
  )

  const debouncedDispatchReferenceId = React.useMemo(
    () => debounce(value => dispatchBookingsAPIParams({ type: REFERENCE_ID, value }), 500),
    [dispatchBookingsAPIParams]
  )

  React.useEffect(
    () => () => {
      debouncedDispatchVenue.cancel()
      debouncedDispatchReferenceId.cancel()
    },
    [debouncedDispatchVenue, debouncedDispatchReferenceId]
  )

  // Extra filters state and handlers
  const [isAddFiltersOpen, setIsAddFiltersOpen] = React.useState(false)
  const addFiltersBtnRef = React.useRef(null)

  const toggleExtraFilter = React.useCallback(
    (filterId, on) => {
      setExtraFieldsEnabled(prev => {
        const current = new Set([...prev])
        if (on) current.add(filterId)
        else current.delete(filterId)
        return Array.from(current)
      })
      if (!on) {
        // Clear the field value and reset the API param when disabling a filter
        setFieldValues(prev => ({ ...prev, [filterId]: '' }))
        const dispatchType = getDispatchPayloadForExtraFilters(filterId)
        if (dispatchType) {
          dispatchBookingsAPIParams({ type: dispatchType, value: '' })
        }
      }
    },
    [setExtraFieldsEnabled, setFieldValues, dispatchBookingsAPIParams]
  )

  const handleClearAllAdditionalFilters = React.useCallback(() => {
    setExtraFieldsEnabled([])
    const keys = extraFilterOptions.map(opt => opt.key)
    keys.forEach(filterKey => {
      const dispatchType = getDispatchPayloadForExtraFilters(filterKey)
      if (dispatchType) {
        dispatchBookingsAPIParams({ type: dispatchType, value: '' })
      }
    })
    setFieldValues(prev => ({
      ...prev,
      assignedCollector: '',
      preferredCollector: '',
      createdBy: '',
      assignedBy: '',
      state: '',
    }))
  }, [setExtraFieldsEnabled, extraFilterOptions, dispatchBookingsAPIParams, setFieldValues])

  // Date picker handlers and state
  const [applyErrorStyling, setApplyErrorStyling] = React.useState({
    startDate: false,
    endDate: false,
  })

  const datePickerFieldConfigMap = React.useMemo(
    () => ({
      START_DATE: {
        key: 'startDateString',
        errorKey: 'startDate',
        dispatchType: BOOKINGS_START_DATE,
        toPayload: date =>
          formatDateForApiCall({
            date,
            startOfDayRatherThanNoonLocalTime: true,
          }),
        label: translate('dateRequestedFrom'),
        inputName: 'bookings_start_start_date_filter',
      },
      END_DATE: {
        key: 'endDateString',
        errorKey: 'endDate',
        dispatchType: BOOKINGS_END_DATE,
        toPayload: date => formatDateForApiCall({ date, endOfDayRatherThanNoonLocalTime: true }),
        label: translate('dateRequestedTo'),
        inputName: 'bookings_start_end_date_filter',
      },
    }),
    [formatDateForApiCall, translate]
  )

  const setError = React.useCallback((errorKey, on) => {
    setApplyErrorStyling(prev => ({ ...prev, [errorKey]: on }))
  }, [])

  const dispatchDateChange = React.useCallback(
    (type, date) => {
      const cfg = datePickerFieldConfigMap[type]
      setError(cfg.errorKey, false)
      dispatchBookingsAPIParams({
        type: cfg.dispatchType,
        value: cfg.toPayload(date),
      })
    },
    [datePickerFieldConfigMap, dispatchBookingsAPIParams, setError]
  )

  const onDateChange = React.useCallback(
    (type, date) => {
      const cfg = datePickerFieldConfigMap[type]
      setFieldValues(prev => ({
        ...prev,
        datePickerValues: { ...prev.datePickerValues, [cfg.key]: date },
      }))

      const isValid = validateDate({ value: date })
      if (!isValid) {
        setError(cfg.errorKey, true)
        return
      }
      dispatchDateChange(type, date)
    },
    [datePickerFieldConfigMap, setError, dispatchDateChange, setFieldValues]
  )

  // Form fields event handlers
  const fieldChangeHandler = React.useCallback(
    (name, value, dispatchType) => {
      setFieldValues(prevState => ({ ...prevState, [name]: value }))
      dispatchBookingsAPIParams({ type: dispatchType, value })
    },
    [setFieldValues, dispatchBookingsAPIParams]
  )

  const onStatusChange = React.useCallback(
    (_, { value }) => {
      setFieldValues(prevState => ({ ...prevState, status: value }))
      dispatchBookingsAPIParams({
        type: BOOKINGS_STATUS,
        value: value.join('_'),
      })
    },
    [setFieldValues, dispatchBookingsAPIParams]
  )

  const onReferenceIdChange = React.useCallback(
    (_, { value }) => {
      setFieldValues(prevState => ({ ...prevState, referenceId: value }))
      debouncedDispatchReferenceId(value)
    },
    [setFieldValues, debouncedDispatchReferenceId]
  )

  const onVenueNameChange = React.useCallback(
    (_, { value }) => {
      setFieldValues(prevState => ({ ...prevState, customerName: value }))
      debouncedDispatchVenue(value)
    },
    [setFieldValues, debouncedDispatchVenue]
  )

  const onResetFilters = React.useCallback(() => {
    resetFieldValues()
    dispatchBookingsAPIParams({ type: RESET_FILTERS })
    setApplyErrorStyling({ startDate: false, endDate: false })
  }, [resetFieldValues, dispatchBookingsAPIParams])

  const preventBlur = React.useCallback(e => {
    e.preventDefault()
  }, [])

  const handleRefreshClick = React.useCallback(async () => {
    if (!onForceRefresh) return
    try {
      setRefreshing(true)
      onForceRefresh()
    } finally {
      setTimeout(() => setRefreshing(false), 300)
    }
  }, [onForceRefresh])

  const disabledBackendPopulatedSearchFilters =
    bookingsListViewSearchFilterOptions?.isFetching ||
    bookingsListViewSearchFilterOptions?.didFetchFail

  return (
    <>
      <div id="bookingsTableFiltersFormContainer">
        <div id="bookingsTableFormActionsContainer">
          <div>
            <Header
              as="h3"
              color="grey"
              content={translate('searchFilters')}
              subheader={translate('bookingsSearchFiltersSubheader')}
            />
          </div>
          <div id="bookingsTableFormActionsContainerRight">
            {isFetching && (
              <div id="bookingsFetchingIndicator" role="status" aria-live="polite" aria-busy="true">
                <Loader active inline size="small" />
                <span>{translate('fetchingBookingsSearchResults')}</span>
              </div>
            )}
            <ExportCSVButton
              dispatch={dispatch}
              translate={translate}
              bookingsAPIParams={bookingsAPIParams}
              localeCode={localeCode}
              bookingsExportList={bookingsExportList}
              collectorsList={collectorsList}
              isDisabled={
                isFetching ||
                !bookingsList ||
                bookingsList.length === 0 ||
                collectorsList.isFetching ||
                collectorsList.didFetchFail
              }
            />
            <Popup
              wide
              open={isAddFiltersOpen}
              onClose={() => setIsAddFiltersOpen(false)}
              on="click"
              position="left center"
              trigger={
                <Button
                  ref={addFiltersBtnRef}
                  type="button"
                  icon
                  basic
                  size="small"
                  onClick={() => setIsAddFiltersOpen(prev => !prev)}
                  title={translate('addFilters')}
                  aria-label={translate('addFilters')}
                  disabled={refreshing || isFetching}
                >
                  <Icon name="filter" />
                  <span id="addFiltersBtnLabel">{translate('addFilters')}</span>
                </Button>
              }
              content={
                <div id="additionalFiltersFormContainer">
                  <Form>
                    {extraFilterOptions.map(opt => (
                      <Form.Field key={opt.key}>
                        <Checkbox
                          label={opt.labelKey}
                          checked={isExtraFilterEnabled(opt.id)}
                          onChange={(_, { checked }) => toggleExtraFilter(opt.id, checked)}
                        />
                      </Form.Field>
                    ))}

                    <div id="additionalFiltersFormContainerActions">
                      <Button
                        size="tiny"
                        basic
                        onClick={() => setExtraFieldsEnabled(extraFilterOptions.map(o => o.id))}
                      >
                        {translate('selectAll')}
                      </Button>
                      <Button size="tiny" basic onClick={() => handleClearAllAdditionalFilters()}>
                        {translate('clearFilters')}
                      </Button>
                      <Button size="tiny" color="green" onClick={() => setIsAddFiltersOpen(false)}>
                        {translate('Close')}
                      </Button>
                    </div>
                  </Form>
                </div>
              }
            />

            <Button
              type="button"
              icon
              basic
              size="small"
              onClick={onResetFilters}
              title={translate('btnLabelResetFilters')}
              aria-label={translate('btnLabelResetFilters')}
              disabled={isResetDisabled || refreshing || isFetching}
            >
              <Icon name="undo" />
              <span id="resetFiltersBtnLabel">{translate('btnLabelResetFilters')}</span>
            </Button>
            <Button
              type="button"
              icon
              basic
              size="small"
              onClick={handleRefreshClick}
              title={translate('Refresh')}
              aria-label={translate('Refresh')}
              loading={refreshing}
              disabled={refreshing || isFetching}
            >
              <Icon name="refresh" />
            </Button>
          </div>
        </div>

        <Form>
          <Form.Group>
            <Form.Field width={4}>
              <label htmlFor="bookings_reference_id_filter">{translate('Reference #')}</label>
              <Input
                id="bookings_reference_id_filter"
                name="bookings_reference_id_filter"
                value={fieldValues.referenceId}
                onChange={onReferenceIdChange}
                icon={
                  fieldValues.referenceId ? (
                    <TextInputClearIcon
                      label={translate('clearValue')}
                      onClick={() => onReferenceIdChange(null, { value: '' })}
                      preventBlur={preventBlur}
                    />
                  ) : null
                }
              />
            </Form.Field>

            <Form.Field width={4}>
              <label htmlFor="bookings_venue_name_filter">{translate('Venue Name')}</label>
              <Input
                id="bookings_venue_name_filter"
                name="bookings_venue_name_filter"
                value={fieldValues.customerName}
                onChange={onVenueNameChange}
                icon={
                  fieldValues.customerName ? (
                    <TextInputClearIcon
                      label={translate('clearValue')}
                      onClick={() => onVenueNameChange(null, { value: '' })}
                      preventBlur={preventBlur}
                    />
                  ) : null
                }
              />
            </Form.Field>

            <Form.Field width={8}>
              <label htmlFor="bookings_status_filter">{translate('Status')}</label>
              <Dropdown
                id="bookings_status_filter"
                selection
                fluid
                value={fieldValues.status}
                placeholder={translate('ALL')}
                options={statusOptions}
                onChange={onStatusChange}
                multiple
                clearable
              />
            </Form.Field>
          </Form.Group>
        </Form>

        <Form>
          <Form.Group>
            <Form.Field width={6}>
              <label htmlFor="bookings_postal_code_filter">{translate('Postcode')}</label>
              <Dropdown
                id="bookings_postal_code_filter"
                selection
                search
                fluid
                clearable
                options={buildFilterOptions(
                  bookingsListViewSearchFilterOptions?.content?.postalCodes,
                  'bookings_postal_code',
                  postalCode => ({
                    key: `bookings_postal_code_${postalCode}`,
                    text: postalCode,
                    value: postalCode,
                  })
                )}
                loading={bookingsListViewSearchFilterOptions?.isFetching}
                disabled={disabledBackendPopulatedSearchFilters}
                placeholder={getDropdownPlaceholder(
                  bookingsListViewSearchFilterOptions?.isFetching,
                  bookingsListViewSearchFilterOptions?.didFetchFail,
                  'loadingPostalCodes',
                  'noPostalCodesAvailable',
                  [
                    ...(bookingsListViewSearchFilterOptions?.content?.postalCodes || []),
                    {
                      key: 'bookings_postal_code_all',
                      text: translate('ALL'),
                      value: '',
                    },
                  ]
                )}
                value={fieldValues.postalCode}
                onChange={(_, { value }) =>
                  fieldChangeHandler('postalCode', value, BOOKINGS_POSTAL_CODE)
                }
              />
              {bookingsListViewSearchFilterOptions?.didFetchFail && (
                <Message negative content={translate('postalCodesLoadError')} />
              )}
            </Form.Field>

            <Form.Field width={5}>
              <label htmlFor={datePickerFieldConfigMap.START_DATE.inputName}>
                {datePickerFieldConfigMap.START_DATE.label}
              </label>
              <ReactDatepicker
                value={fieldValues.datePickerValues.startDateString}
                onChange={date => onDateChange('START_DATE', date)}
                name={datePickerFieldConfigMap.START_DATE.inputName}
                showMonthDropdown
                showYearDropdown
                // Provide no-op onBlur function to avoid unwanted API
                // calls on blur (ReactDatePicker component-specific behavior)
                onBlur={() => {}}
                className={[
                  'wide-date-field',
                  applyErrorStyling.startDate ? 'input-error-semantic-ui' : '',
                ].join(' ')}
                minDate={moment('01/01/1985', defaultDisplayedDateFormat)}
                maxDate={moment(
                  fieldValues.datePickerValues.endDateString,
                  defaultDisplayedDateFormat
                )}
              />
              {applyErrorStyling.startDate && (
                <Message negative content={translate('invalidDate')} />
              )}
            </Form.Field>

            <Form.Field width={5}>
              <label htmlFor={datePickerFieldConfigMap.END_DATE.inputName}>
                {datePickerFieldConfigMap.END_DATE.label}
              </label>
              <ReactDatepicker
                value={fieldValues.datePickerValues.endDateString}
                onChange={date => onDateChange('END_DATE', date)}
                name={datePickerFieldConfigMap.END_DATE.inputName}
                showMonthDropdown
                showYearDropdown
                onBlur={() => {}}
                className={applyErrorStyling.endDate ? 'input-error-semantic-ui' : ''}
                minDate={moment(
                  fieldValues.datePickerValues.startDateString,
                  defaultDisplayedDateFormat
                )}
                maxDate={moment().endOf('day')}
              />
              {applyErrorStyling.endDate && <Message negative content={translate('invalidDate')} />}
            </Form.Field>
          </Form.Group>
        </Form>

        {extraFieldsEnabled.length > 0 && (
          <Form>
            <Form.Group>
              {isExtraFilterEnabled('assignedCollector') && (
                <Form.Field width="4" error={collectorsList?.didFetchFail}>
                  <label htmlFor="bookings_assigned_collector_filter">
                    {translate('assignedCollector')}
                  </label>
                  <Dropdown
                    id="bookings_assigned_collector_filter"
                    selection
                    search
                    fluid
                    clearable
                    options={collectorOptions}
                    loading={collectorsList?.isFetching}
                    disabled={collectorsList?.isFetching || collectorsList?.didFetchFail}
                    placeholder={getDropdownPlaceholder(
                      collectorsList?.isFetching,
                      collectorsList?.didFetchFail,
                      'loadingCollectors',
                      'noCollectorsAvailable',
                      [
                        ...collectorOptions,
                        {
                          key: 'bookings_assigned_collector_all',
                          text: translate('ALL'),
                          value: '',
                        },
                      ]
                    )}
                    value={fieldValues.assignedCollector}
                    onChange={(_, { value }) =>
                      fieldChangeHandler('assignedCollector', value, BOOKINGS_ASSIGNED_COLLECTOR)
                    }
                  />
                  {collectorsList?.didFetchFail && (
                    <Message negative content={translate('collectorsLoadError')} />
                  )}
                </Form.Field>
              )}

              {isExtraFilterEnabled('preferredCollector') && (
                <Form.Field width="4" error={collectorsList?.didFetchFail}>
                  <label htmlFor="bookings_preferred_collector_filter">
                    {translate('preferredCollector')}
                  </label>
                  <Dropdown
                    id="bookings_preferred_collector_filter"
                    selection
                    search
                    fluid
                    clearable
                    options={collectorOptions}
                    loading={collectorsList?.isFetching}
                    disabled={collectorsList?.isFetching || collectorsList?.didFetchFail}
                    placeholder={getDropdownPlaceholder(
                      collectorsList?.isFetching,
                      collectorsList?.didFetchFail,
                      'loadingCollectors',
                      'noCollectorsAvailable',
                      [
                        ...collectorOptions,
                        {
                          key: 'bookings_preferred_collector_all',
                          text: translate('ALL'),
                          value: '',
                        },
                      ]
                    )}
                    value={fieldValues.preferredCollector}
                    onChange={(_, { value }) =>
                      fieldChangeHandler('preferredCollector', value, BOOKINGS_PREFERRED_COLLECTOR)
                    }
                  />
                  {collectorsList?.didFetchFail && (
                    <Message negative content={translate('collectorsLoadError')} />
                  )}
                </Form.Field>
              )}

              {isExtraFilterEnabled('createdBy') && (
                <Form.Field width="3">
                  <label htmlFor="bookings_created_by_filter">{translate('createdBy')}</label>
                  <Dropdown
                    id="bookings_created_by_filter"
                    selection
                    search
                    fluid
                    clearable
                    loading={bookingsListViewSearchFilterOptions?.isFetching}
                    value={fieldValues.createdBy}
                    placeholder={getDropdownPlaceholder(
                      bookingsListViewSearchFilterOptions?.isFetching,
                      bookingsListViewSearchFilterOptions?.didFetchFail,
                      'loadingCreatedBy',
                      'noCreatorsAvailable',
                      [
                        ...(bookingsListViewSearchFilterOptions?.content?.createdByUsers || []),
                        {
                          key: 'bookings_created_by_venue_app',
                          text: translate('venueApp'),
                          value: 'venue_app',
                        },
                      ]
                    )}
                    options={buildFilterOptions(
                      bookingsListViewSearchFilterOptions?.content?.createdByUsers,
                      'bookings_created_by',
                      createdByUser => ({
                        key: `bookings_created_by_${createdByUser.id}`,
                        text: `${createdByUser.firstName} ${createdByUser.lastName}`,
                        value: createdByUser.id,
                      }),
                      [
                        {
                          key: 'bookings_created_by_venue_app',
                          text: translate('venueApp'),
                          value: 'venue_app',
                        },
                      ]
                    )}
                    onChange={(_, { value }) =>
                      fieldChangeHandler('createdBy', value, BOOKINGS_CREATED_BY)
                    }
                    disabled={disabledBackendPopulatedSearchFilters}
                  />
                  {bookingsListViewSearchFilterOptions?.didFetchFail && (
                    <Message negative content={translate('createdByLoadError')} />
                  )}
                </Form.Field>
              )}

              {isExtraFilterEnabled('assignedBy') && (
                <Form.Field width="3">
                  <label htmlFor="bookings_assigned_by_filter">{translate('assignedBy')}</label>
                  <Dropdown
                    id="bookings_assigned_by_filter"
                    selection
                    search
                    fluid
                    clearable
                    value={fieldValues.assignedBy}
                    placeholder={getDropdownPlaceholder(
                      bookingsListViewSearchFilterOptions?.isFetching,
                      bookingsListViewSearchFilterOptions?.didFetchFail,
                      'loadingAssignedBy',
                      'noAssignedByAvailable',
                      [
                        ...(bookingsListViewSearchFilterOptions?.content?.assignedByUsers || []),
                        {
                          key: 'bookings_assigned_by_auto_assigned',
                          text: translate('autoAssigned'),
                          value: 'auto_assigned',
                        },
                      ]
                    )}
                    loading={bookingsListViewSearchFilterOptions?.isFetching}
                    options={buildFilterOptions(
                      bookingsListViewSearchFilterOptions?.content?.assignedByUsers,
                      'bookings_assigned_by',
                      assignedByUser => ({
                        key: `bookings_assigned_by_${assignedByUser.id}`,
                        text: `${assignedByUser.firstName} ${assignedByUser.lastName}`,
                        value: assignedByUser.id,
                      }),
                      [
                        {
                          key: 'bookings_assigned_by_auto_assigned',
                          text: translate('autoAssigned'),
                          value: 'auto_assigned',
                        },
                      ]
                    )}
                    onChange={(_, { value }) =>
                      fieldChangeHandler('assignedBy', value, BOOKINGS_ASSIGNED_BY)
                    }
                    disabled={disabledBackendPopulatedSearchFilters}
                  />
                  {bookingsListViewSearchFilterOptions?.didFetchFail && (
                    <Message negative content={translate('assignedByLoadError')} />
                  )}
                </Form.Field>
              )}

              {isExtraFilterEnabled('state') && (
                <Form.Field width="2">
                  <label htmlFor="bookings_state_filter">{translate('State')}</label>
                  <Dropdown
                    id="bookings_state_filter"
                    selection
                    search
                    fluid
                    value={fieldValues.state}
                    placeholder={getDropdownPlaceholder(
                      bookingsListViewSearchFilterOptions?.isFetching,
                      bookingsListViewSearchFilterOptions?.didFetchFail,
                      'loadingStates',
                      'noStatesAvailable',
                      [
                        ...(bookingsListViewSearchFilterOptions?.content?.states || []),
                        {
                          key: 'bookings_state_all',
                          text: translate('ALL'),
                          value: '',
                        },
                      ]
                    )}
                    options={buildFilterOptions(
                      bookingsListViewSearchFilterOptions?.content?.states,
                      'bookings_state',
                      state => ({
                        key: `bookings_state_${state}`,
                        text: state,
                        value: state,
                      })
                    )}
                    loading={bookingsListViewSearchFilterOptions?.isFetching}
                    onChange={(_, { value }) =>
                      fieldChangeHandler('state', value, BOOKINGS_VENUE_STATE)
                    }
                    clearable
                    disabled={disabledBackendPopulatedSearchFilters}
                  />
                  {bookingsListViewSearchFilterOptions?.didFetchFail && (
                    <Message negative content={translate('statesLoadError')} />
                  )}
                </Form.Field>
              )}
            </Form.Group>
          </Form>
        )}
      </div>
    </>
  )
}

export default React.memo(KegCollectionBookingsTableFiltersForm)
